import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid2 as Grid,
  Button,
  Avatar,
  Divider,
  IconButton,
  Alert,
  Tabs,
  Tab,
  MenuItem,
  Switch,
  FormControlLabel,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Save as SaveIcon,
  CameraAlt as CameraIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar?: string;
  role: string;
  department: string;
  joinDate: string;
  lastLogin: string;
}

interface UserSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  twoFactorAuth: boolean;
  darkMode: boolean;
  language: string;
}

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savingPassword, setSavingPassword] = useState(false);

  // Mock user data
  const [profile, setProfile] = useState<UserProfile>({
    id: '1',
    name: 'Administrador Pagali',
    email: 'admin@pagali.cv',
    phone: '+238 9123456',
    role: 'Administrador',
    department: 'Tecnologia',
    joinDate: '2023-01-15',
    lastLogin: new Date().toISOString(),
  });

  const [settings, setSettings] = useState<UserSettings>({
    emailNotifications: true,
    pushNotifications: true,
    twoFactorAuth: false,
    darkMode: false,
    language: 'pt',
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const recentActivities = [
    { action: 'Login no sistema', timestamp: new Date().toISOString() },
    { action: 'Criou novo utilizador: Maria Silva', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() },
    { action: 'Actualizou configuração SISP', timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString() },
    { action: 'Exportou relatório de pagamentos', timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString() },
  ];

  const handleProfileSave = () => {
    // In production, this would make an API call
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setSaveError(t('profile.passwordMismatch'));
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setSaveError(t('profile.passwordRequirements'));
      return;
    }
    setSavingPassword(true);
    setSaveError(null);
    try {
      const userId = user?.id;
      if (!userId) throw new Error('Utilizador não identificado');
      await api.put(`/admin/users/${userId}/password`, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setSaveSuccess(true);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || 'Erro ao alterar senha');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSettingsSave = () => {
    // In production, this would make an API call
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
        {t('profile.title')}
      </Typography>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSaveSuccess(false)}>
          {t('profile.changesSaved')}
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                <Avatar
                  sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                  src={profile.avatar}
                >
                  {profile.name.charAt(0)}
                </Avatar>
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    right: -8,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  <CameraIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="h6" fontWeight={600}>
                {profile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {profile.role}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {profile.department}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="caption" color="text.secondary">
                  {t('profile.registrationDate')}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  {new Date(profile.joinDate).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('profile.lastAccess')}
                </Typography>
                <Typography variant="body2">
                  {new Date(profile.lastLogin).toLocaleString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon fontSize="small" />
                {t('profile.recentActivity')}
              </Typography>
              <List dense>
                {recentActivities.map((activity, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemText
                      primary={
                        <Typography variant="caption">
                          {activity.action}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {new Date(activity.timestamp).toLocaleString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Main Content */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              variant="fullWidth"
            >
              <Tab icon={<PersonIcon />} label={t('profile.personalInfo')} iconPosition="start" />
              <Tab icon={<LockIcon />} label={t('profile.security')} iconPosition="start" />
              <Tab icon={<SettingsIcon />} label={t('profile.preferences')} iconPosition="start" />
            </Tabs>

            <CardContent sx={{ p: 3 }}>
              {/* Personal Information Tab */}
              {tabValue === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('profile.personalInfo')}
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label={t('profile.fullName')}
                        value={profile.name}
                        onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                        InputProps={{
                          startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={profile.email}
                        onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                        InputProps={{
                          startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label={t('common.phone')}
                        value={profile.phone}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        InputProps={{
                          startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label={t('profile.role')}
                        value={profile.role}
                        onChange={(e) => setProfile({ ...profile, role: e.target.value })}
                        disabled
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label={t('profile.department')}
                        value={profile.department}
                        onChange={(e) => setProfile({ ...profile, department: e.target.value })}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleProfileSave}
                    >
                      {t('profile.saveChanges')}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Security Tab */}
              {tabValue === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('profile.changePassword')}
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        type="password"
                        label={t('profile.currentPassword')}
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        type="password"
                        label={t('profile.newPassword')}
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        type="password"
                        label={t('profile.confirmPassword')}
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      />
                    </Grid>
                  </Grid>

                  <Alert severity="info" sx={{ mt: 3 }}>
                    {t('profile.passwordRequirements')}
                  </Alert>

                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={savingPassword ? <CircularProgress size={18} color="inherit" /> : <LockIcon />}
                      onClick={handlePasswordChange}
                      disabled={!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword || savingPassword}
                    >
                      {t('profile.changePassword')}
                    </Button>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SecurityIcon />
                    {t('profile.twoFactor')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {t('profile.twoFactorDescription')}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.twoFactorAuth}
                        onChange={(e) => setSettings({ ...settings, twoFactorAuth: e.target.checked })}
                      />
                    }
                    label={t('profile.twoFactorEnable')}
                  />
                </Box>
              )}

              {/* Preferences Tab */}
              {tabValue === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('profile.systemPreferences')}
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <NotificationsIcon fontSize="small" />
                        {t('notifications.title')}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.emailNotifications}
                            onChange={(e) => setSettings({ ...settings, emailNotifications: e.target.checked })}
                          />
                        }
                        label={t('profile.emailNotifications')}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.pushNotifications}
                            onChange={(e) => setSettings({ ...settings, pushNotifications: e.target.checked })}
                          />
                        }
                        label={t('profile.pushNotifications')}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        {t('profile.appearance')}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.darkMode}
                            onChange={(e) => setSettings({ ...settings, darkMode: e.target.checked })}
                          />
                        }
                        label={t('profile.darkMode')}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Divider sx={{ my: 2 }} />
                      <TextField
                        fullWidth
                        select
                        label={t('profile.language')}
                        value={settings.language}
                        onChange={(e) => setSettings({ ...settings, language: e.target.value })}
                      >
                        <MenuItem value="pt">Português</MenuItem>
                        <MenuItem value="en">English</MenuItem>
                        <MenuItem value="fr">Français</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSettingsSave}
                    >
                      {t('profile.savePreferences')}
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;
